import {
    CopilotChatInput,
    useAgent,
    useCopilotChatConfiguration,
    type CopilotChatInputProps,
} from '@copilotkit/react-core/v2'
import { IconMessageCircleUp } from '@tabler/icons-react'
import { HITL_CANCELLED_RESULT, pendingHitlToolCallIds } from './hitlCancel'

// CopilotChat `input` slot. The user is allowed to send a message instead of
// answering a pending ask_user_question (HITL) widget — but the open call can't be
// left unanswered (a stranded call corrupts the thread: Gemini 400 next turn /
// reload crash, see ChoicePrompt). So on submit we append a "dismissed" tool
// result for every pending HITL call, then hand off to CopilotChat's default
// send (`agent.addMessage(userMessage)` + `runAgent`). Because both happen
// before the single run, the cancellations and the user's message reach the
// backend as ONE batched turn — the agent sees the dismissals and the new
// message together, and answers once.
export function HitlGuardedInput(props: CopilotChatInputProps) {
    const agentId = useCopilotChatConfiguration()?.agentId
    const { agent } = useAgent(agentId ? { agentId } : undefined)

    const onSubmitMessage = (value: string) => {
        for (const toolCallId of pendingHitlToolCallIds(agent.messages)) {
            agent.addMessage({
                id: crypto.randomUUID(),
                role: 'tool',
                content: HITL_CANCELLED_RESULT,
                toolCallId,
            })
        }
        // Default send: appends the user message and runs once — the tool
        // results queued above ride along in the same run.
        props.onSubmitMessage?.(value)
    }

    return (
        <CopilotChatInput
            {...props}
            onSubmitMessage={onSubmitMessage}
            sendButton={{
                children: <IconMessageCircleUp />,
                // Icon-only button: a screen reader otherwise announces an
                // unnamed button (WCAG 4.1.2). title doubles as a hover tooltip.
                'aria-label': 'Send message',
                title: 'Send message',
            }}
        />
    )
}
